import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Button,
    Grid,
    Paper,
    MenuItem,
    Autocomplete,
    Box,
    Divider,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
    Popper,
    ClickAwayListener
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { UpdateToDo, getToDoPrioritys, getToDoStatus, getTodoById, getChecklistGroupsForTodo, getAllTodoCategories } from 'core/API/todo';
import { getUsersByWorkplace } from 'core/API/checklist';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useWorkplaceUser } from 'module/documents/utils';
import styles from "../addToDo/AddToDo.module.scss";
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    safeDateParse, 
    formatDateForInput, 
    formatDateForPayload, 
    formatDateMMDDYYYY,
    getTodayISO,
    validateDateNotPast,
    formatDatePickerForInput
} from 'shared/utils/dateUtils';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
    getCategoryIri,
    getDefaultTodoCategoryIri,
} from 'module/toDoPage/utils/todoCategoryUtils';

type RouteParams = { id: string };

const EditToDo: React.FC = () => {
    const { showToast } = useToastContext();
    const queryClient = useQueryClient();
    const history = useHistory();
    const workplace = useWorkplaceUser();
    const { id } = useParams<RouteParams>();

    // Robust ID resolver for API-style strings like "/api/to_dos/123"
    const parseApiPathId = (value: any): number | null => {
        if (value == null) return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'string') {
            const parts = value.split('/');
            const last = parts[parts.length - 1];
            const n = Number(last);
            return Number.isFinite(n) ? n : null;
        }
        if (typeof value === 'object' && typeof (value as any).id !== 'undefined') {
            const n = Number((value as any).id);
            return Number.isFinite(n) ? n : null;
        }
        return null;
    };

    const [todoId, setTodoId] = useState<number | null>(null);

    const userId = Number(localStorage.getItem('userId')) || (workplace as any)?.data?.user?.id || 0;
    const workplaceId = Number(localStorage.getItem('workplace-id')) || (workplace as any)?.data?.workplace?.id || 1;

    const [form, setForm] = useState({
        description: '',
        dueDate: '',
        dueTime: null as dayjs.Dayjs | null,
        noteMessage: '',
        priorityId: 1,
        statusId: 1,
        categoryIri: '',
        geolocation: {
            latitude: '',
            longitude: '',
        },
    });

    const [isFormDirty, setIsFormDirty] = useState(false);
    const [errors, setErrors] = useState<{ description?: string; dueDate?: string; assigned?: string; category?: string; noteMessage?: string }>({});
    const [openBackDialog, setOpenBackDialog] = useState(false);

    const [assignmentType, setAssignmentType] = useState<'Person' | 'Group'>('Person');
    const [selectedValue, setSelectedValue] = useState<any>('');
    const [calendarOpen, setCalendarOpen] = useState(false);
    const calendarAnchorRef = useRef<HTMLDivElement>(null);

    // Helper function to format date for display (MM/DD/YYYY)
    const formatDateForDisplay = (dateString: string): string => {
        return formatDateMMDDYYYY(dateString);
    };

    // Calendar change handler
    const handleCalendarChange = (val: Date | null) => {
        if (val) {
            const newISO = formatDatePickerForInput(val);
            setForm(prev => ({ ...prev, dueDate: newISO }));
            setIsFormDirty(true);
        }
        setCalendarOpen(false);
    };

    // Fetch existing todo data
    const { data: existingTodo, isLoading: isLoadingTodo, error: todoError } = useQuery(
        ['todo', id],
        () => getTodoById(Number(id)),
        {
            enabled: !!id,
            onSuccess: (todo) => {
                // Resolve and store reliable numeric ID for updates
                const resolvedId =
                    parseApiPathId(todo?.id) ??
                    parseApiPathId((todo as any)?.['@id']) ??
                    parseApiPathId(id);
                if (resolvedId != null) {
                    setTodoId(resolvedId);
                }
                
                // Pre-fill form with existing data
                if (todo.dueDate || todo.due_date) {
                    const dateStr = formatDateForInput(todo.dueDate || todo.due_date);
                    
                    // Extract time from dueTime field (format: "08:33:00" -> "08:33")
                    let timeStr = null;
                    if (todo.dueTime) {
                        timeStr = dayjs(todo.dueTime, 'HH:mm:ss')
                    }
                    console.log("timeStr", todo.dueTime, timeStr);
                    
                    setForm({
                        description: todo.description || '',
                        dueDate: dateStr,
                        dueTime: timeStr,
                        noteMessage: todo.note?.message || todo.note || '',
                        priorityId: todo.priority?.id || (typeof todo.priority === 'string' ? parseInt(todo.priority.split('/').pop()) : 1),
                        statusId: todo.status?.id || (typeof todo.status === 'string' ? parseInt(todo.status.split('/').pop()) : 1),
                        categoryIri: getCategoryIri(todo.category),
                        geolocation: {
                            latitude: todo.geolocation && todo.geolocation[0] ? todo.geolocation[0].split(',')[0] : '',
                            longitude: todo.geolocation && todo.geolocation[0] ? todo.geolocation[0].split(',')[1] : '',
                        },
                    });
                } else {
                    setForm({
                        description: todo.description || '',
                        dueDate: getTodayISO(), // Set today as default if no due date
                        dueTime: null,
                        noteMessage: todo.note?.message || todo.note || '',
                        priorityId: todo.priority?.id || (typeof todo.priority === 'string' ? parseInt(todo.priority.split('/').pop()) : 1),
                        statusId: todo.status?.id || (typeof todo.status === 'string' ? parseInt(todo.status.split('/').pop()) : 1),
                        categoryIri: getCategoryIri(todo.category),
                        geolocation: {
                            latitude: todo.geolocation && todo.geolocation[0] ? todo.geolocation[0].split(',')[0] : '',
                            longitude: todo.geolocation && todo.geolocation[0] ? todo.geolocation[0].split(',')[1] : '',
                        },
                    });
                }
                
                // Handle assignment type based on API response structure
                if (todo && (todo.assignedGroup || todo.group)) {
                    const groupObj = todo.assignedGroup || todo.group;
                    setAssignmentType('Group');
                    if (typeof groupObj === 'string') {
                        const groupId = groupObj.split('/').pop();
                        setSelectedValue(parseInt(groupId as string));
                    } else if (groupObj && groupObj.id) {
                        setSelectedValue(groupObj.id);
                    } else {
                        setSelectedValue('');
                    }
                } else if (todo && (todo.user || todo.assignee)) {
                    const userObj = todo.user || todo.assignee;
                    setAssignmentType('Person');
                    if (typeof userObj === 'string') {
                        const uid = userObj.split('/').pop();
                        setSelectedValue(parseInt(uid as string));
                    } else if (userObj && userObj.id) {
                        setSelectedValue(userObj.id);
                    } else {
                        setSelectedValue('');
                    }
                } else {
                    setAssignmentType('Person');
                    setSelectedValue('');
                }
            }
        }
    );

    // Fetch dropdown data
    const { data: prioritysList, isLoading: isPrioritiesLoading } = useQuery(
        ['todo-priorities', workplaceId],
        () => getToDoPrioritys(),
        {
            enabled: true,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const { data: statusList, isLoading: isStatusesLoading } = useQuery(
        ['todo-statuses', workplaceId],
        () => getToDoStatus(),
        {
            enabled: true,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const { data: usersList, isLoading: isUsersLoading } = useQuery(
        ['todo-users', workplaceId],
        () => getUsersByWorkplace(workplaceId),
        {
            enabled: !!workplaceId,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    // Fetch groups from API
    const { data: groupsList, isLoading: isGroupsLoading } = useQuery(
        ['todo-groups', workplaceId],
        () => getChecklistGroupsForTodo(),
        {
            enabled: !!workplaceId,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const { data: todoCategories, isLoading: isCategoriesLoading } = useQuery(
        ['todo-categories', workplaceId],
        () => getAllTodoCategories(),
        {
            enabled: !!workplaceId,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    // Normalize API responses
    const normalizedPriorities = Array.isArray(prioritysList?.member)
        ? prioritysList.member
        : Array.isArray(prioritysList?.['hydra:member'])
        ? prioritysList['hydra:member']
        : Array.isArray(prioritysList?.items)
        ? prioritysList.items
        : Array.isArray(prioritysList?.data)
        ? prioritysList.data
        : Array.isArray(prioritysList)
        ? prioritysList
        : [];

    const normalizedStatuses = Array.isArray(statusList?.member)
        ? statusList.member
        : Array.isArray(statusList?.['hydra:member'])
        ? statusList['hydra:member']
        : Array.isArray(statusList?.items)
        ? statusList.items
        : Array.isArray(statusList?.data)
        ? statusList.data
        : Array.isArray(statusList)
        ? statusList
        : [];

    // Normalize groups data - prefer explicit arrays from API shapes
    const normalizedGroups = Array.isArray((groupsList as any)?.member)
        ? (groupsList as any).member
        : Array.isArray((groupsList as any)?.['hydra:member'])
        ? (groupsList as any)['hydra:member']
        : Array.isArray((groupsList as any)?.items)
        ? (groupsList as any).items
        : Array.isArray((groupsList as any)?.data)
        ? (groupsList as any).data
        : Array.isArray(groupsList as any)
        ? (groupsList as any)
        : [];

    // Workplace-scoped users from /workplaces/users
    const normalizedUsers = Array.isArray(usersList) ? usersList : [];

    const noPriorities = !isPrioritiesLoading && normalizedPriorities.length === 0;
    const noStatuses = !isStatusesLoading && normalizedStatuses.length === 0;
    const noGroups = !isGroupsLoading && normalizedGroups.length === 0;
    const normalizedCategories = Array.isArray(todoCategories) ? todoCategories : [];
    const noCategories = !isCategoriesLoading && normalizedCategories.length === 0;
    const selectedCategory =
        normalizedCategories.find((cat: any) => getCategoryIri(cat) === form.categoryIri) ?? null;

    useEffect(() => {
        if (isCategoriesLoading || normalizedCategories.length === 0 || form.categoryIri) {
            return;
        }
        const defaultCategoryIri = getDefaultTodoCategoryIri(normalizedCategories);
        if (defaultCategoryIri) {
            setForm((prev) => ({ ...prev, categoryIri: defaultCategoryIri }));
        }
    }, [isCategoriesLoading, normalizedCategories, form.categoryIri]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setIsFormDirty(true);

        if (name === 'latitude' || name === 'longitude') {
            setForm((prev) => ({
                ...prev,
                geolocation: {
                    ...prev.geolocation,
                    [name]: value,
                },
            }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleAssignmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAssignmentType(e.target.value as 'Person' | 'Group');
        setIsFormDirty(true);
        setSelectedValue('');
        setErrors(prev => ({ ...prev, assigned: '' }));
    };

    const handleBackClick = () => {
        if (isFormDirty) {
            setOpenBackDialog(true);
        } else {
            history.push(ROUTES.TO_DO);
        }
    };

    const handleDialogClose = (save: boolean) => {
        setOpenBackDialog(false);
        if (save) {
            handleSubmit({ preventDefault: () => {} } as React.FormEvent);
        } else {
            history.push(ROUTES.TO_DO);
        }
    };

    const validateDueDate = (date: string, time: string) => {
        if (!date) return false;
        return validateDateNotPast(date, true);
    };

    // Enhanced validation for edit mode (inline errors like Logbook)
    const validateForm = () => {
        const nextErrors: { description?: string; dueDate?: string; assigned?: string; category?: string; noteMessage?: string } = {};
        if (!form.description.trim()) {
            nextErrors.description = 'Description is required';
        } else if (form.description.length > 250) {
            nextErrors.description = 'Description must be ≤ 250 characters';
        }
        if (!form.dueDate) {
            nextErrors.dueDate = 'Due date is required';
        }
        if (!selectedValue) {
            nextErrors.assigned = `Please select a ${assignmentType === 'Person' ? 'person' : 'group'}`;
        }
        if (!form.categoryIri) {
            nextErrors.category = 'Category is required';
        }
        if (form.noteMessage && form.noteMessage.length > 500) {
            nextErrors.noteMessage = 'Notes must be ≤ 500 characters';
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const { mutate: updateToDo, isLoading } = useMutation(
        (data: any) => {
            const resolved = todoId ?? parseApiPathId(id) ?? 0;
            return UpdateToDo(resolved, data);
        },
        {
            onSuccess: () => {
                showToast('success', 'ToDo updated successfully!');
                try {
                    queryClient.invalidateQueries(['todos', workplaceId]);
                } catch (e) {
                    console.warn('Failed to invalidate todos cache after update', e);
                }
                history.push(ROUTES.TO_DO);
            },
            onError: (error) => {
                console.error('Todo update failed:', error);
                showToast('error', 'Failed to update ToDo');
            },
        }
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Use enhanced validation
        if (!validateForm()) {
            return;
        }

        // Use the imported formatDateForPayload function

        const localWorkplaceId = Number(localStorage.getItem('workplace-id')) || workplaceId;
        const body: any = {
            description: form.description,
            log: null,
            dueDate: formatDateForPayload(form.dueDate), // Format as MM/DD/YYYY for payload
            dueTime: form.dueTime ? form.dueTime.format('HH:mm:ss') : null,
            note: form.noteMessage || "",
            organization: "/api/organizations/1", // Static organization ID
            geolocation: [form.geolocation.latitude && form.geolocation.longitude ? `${form.geolocation.latitude},${form.geolocation.longitude}` : "28.6139,77.2090"],
            workplace: `/api/workplaces/${localWorkplaceId}`,
            priority: `/api/priorities/${form.priorityId}`, // Dynamic priority endpoint
            status: `/api/statuses/${form.statusId}`, // Dynamic status endpoint
            category: form.categoryIri,
        };

        // Set user or group based on assignment type
        if (assignmentType === 'Person') {
            body.user = `/api/users/${selectedValue}`;
            body.assignedGroup = null; // Set assignedGroup to null when user is selected
        } else {
            body.assignedGroup = `/api/checklist_groups/${selectedValue}`; // Use assignedGroup field as requested
            body.user = null; // Set user to null when group is selected
        }

        try {
            updateToDo(body);
        } catch (error) {
            console.error('Error submitting todo update:', error);
            showToast('error', 'Failed to submit todo update. Please try again.');
        }
    };

    if (isLoadingTodo) {
        return (
            <Box sx={{ width: "100%", height: "50vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (todoError) {
        return (
            <Box sx={{ width: "100%", height: "50vh", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                <Typography variant="h6" color="error" mb={2}>
                    Error loading ToDo
                </Typography>
                <Button 
                    onClick={() => history.push(ROUTES.TO_DO)}
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                >
                    Back to ToDo List
                </Button>
            </Box>
        );
    }

    if (!existingTodo) {
        return (
            <Box sx={{ width: "100%", height: "50vh", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                <Typography variant="h6" color="error" mb={2}>
                    ToDo not found
                </Typography>
                <Button 
                    onClick={() => history.push(ROUTES.TO_DO)}
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                >
                    Back to ToDo List
                </Button>
            </Box>
        );
    }

    return (
        <Paper elevation={1} style={{ padding: '24px', maxWidth: "100%", margin: '0 auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                    onClick={handleBackClick}
                >
                    Back
                </Button>
                <Button
                    type="submit"
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', padding: '5px 12px' }}
                    disabled={isLoading || Object.keys(errors).length > 0 || !form.description || !form.dueDate || !selectedValue}
                    onClick={handleSubmit}
                >
                    {isLoading ? 'Updating...' : 'Update'}
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                Edit To-Do
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box className={styles.selectionBox}>
                            {/* Subheading removed to match Create page */}
                            <RadioGroup
                                row
                                value={assignmentType}
                                onChange={handleAssignmentChange}
                                className={styles.radioGroup}
                            >
                                <FormControlLabel 
                                    value="Person" 
                                    control={<Radio 
                                        sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }}
                                        disabled={isLoading}
                                    />} 
                                    label="Person"
                                    disabled={isLoading}
                                />
                                <FormControlLabel 
                                    value="Group" 
                                    control={<Radio 
                                        sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }}
                                        disabled={isLoading}
                                    />} 
                                    label="Group"
                                    disabled={isLoading}
                                />
                            </RadioGroup>
                            <TextField
                                select
                                label={assignmentType === 'Person' ? 'Select Person' : 'Select Group'}
                                value={selectedValue || ''}
                                onChange={(e) => {
                                    const value = Number((e.target as HTMLInputElement).value);
                                    setSelectedValue(value);
                                    setIsFormDirty(true);
                                }}
                                fullWidth
                                required
                                disabled={assignmentType === 'Person' ? isUsersLoading : isGroupsLoading || isLoading}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            style: { maxHeight: 320, overflowY: 'auto' },
                                        },
                                    },
                                    displayEmpty: true,
                                }}
                            >
                                {!selectedValue && (
                                    <MenuItem value="" disabled>
                                        {assignmentType === 'Person' ? 'Select Person' : 'Select Group'}
                                    </MenuItem>
                                )}
                                {assignmentType === 'Person' ? (
                                    isUsersLoading ? (
                                        <MenuItem disabled value="">
                                            Loading users...
                                        </MenuItem>
                                    ) : (
                                        normalizedUsers
                                            .sort((a: any, b: any) => {
                                                // Sort by first name first, then by last name
                                                const firstNameA = (a.firstName || '').toLowerCase();
                                                const firstNameB = (b.firstName || '').toLowerCase();
                                                const lastNameA = (a.lastName || '').toLowerCase();
                                                const lastNameB = (b.lastName || '').toLowerCase();
                                                
                                                if (firstNameA !== firstNameB) {
                                                    return firstNameA.localeCompare(firstNameB);
                                                }
                                                return lastNameA.localeCompare(lastNameB);
                                            })
                                            .map((item: any) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {`${item.firstName || ''} ${item.lastName || ''}`.trim()}
                                                </MenuItem>
                                            ))
                                    )
                                ) : (
                                    isGroupsLoading ? (
                                        <MenuItem disabled value="">
                                            Loading groups...
                                        </MenuItem>
                                    ) : noGroups ? (
                                        <MenuItem disabled value="">
                                            No groups found
                                        </MenuItem>
                                    ) : (
                                        normalizedGroups.map((item: any) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.label || item.name}
                                            </MenuItem>
                                        ))
                                    )
                                )}
                            </TextField>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            name="description"
                            label="Description"
                            fullWidth
                            required
                            value={form.description}
                            onChange={handleChange}
                            inputProps={{ maxLength: 250 }}
                            disabled={isLoading}
                        />
                        {errors.description && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.description}
                            </Typography>
                        )}
                    </Grid>
                    
                    <Grid item xs={6}>
                        <TextField
                            name="dueDate"
                            label="Due Date"
                            fullWidth
                            required
                            value={form.dueDate ? formatDateForDisplay(form.dueDate) : ''}
                            placeholder="MM/DD/YYYY"
                            InputLabelProps={{ shrink: true }}
                            disabled={isLoading}
                            error={!!errors.dueDate}
                            helperText={errors.dueDate}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    fontSize: '0.95rem',
                                    backgroundColor: isLoading ? '#f5f5f5' : '#fff',
                                    color: isLoading ? '#999' : '#3c5471',
                                    fontFamily: 'Gotham',
                                    '& fieldset': {
                                        border: '1px solid rgba(0,0,0,0.23)',
                                    },
                                    '&:hover fieldset': {
                                        border: '1px solid rgba(0,0,0,0.23)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: '1px solid rgba(0,0,0,0.23)',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#f5f5f5',
                                        color: '#999',
                                    },
                                },
                                '& input': {
                                    fontFamily: 'Gotham',
                                    color: isLoading ? '#999' : '#3c5471',
                                    '&.Mui-disabled': {
                                        color: '#999',
                                    },
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            disabled={isLoading}
                                            onClick={() => !isLoading && setCalendarOpen(true)} 
                                            size="small"
                                            sx={{ 
                                                color: isLoading ? '#999' : '#3c5471',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                                }
                                            }}
                                        >
                                            <CalendarTodayIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {errors.assigned && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.assigned}
                            </Typography>
                        )}

                        {/* Calendar Popper */}
                        <Popper
                            open={calendarOpen && !isLoading}
                            anchorEl={calendarAnchorRef.current}
                            placement="top-start"
                            style={{ 
                                zIndex: 999999999999,
                                position: 'fixed',
                                top: '20px',
                                left: '300px'
                            }}
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -8],
                                    },
                                },
                                {
                                    name: 'preventOverflow',
                                    options: {
                                        boundary: 'viewport',
                                        altBoundary: true,
                                    },
                                },
                                {
                                    name: 'flip',
                                    options: {
                                        fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
                                    },
                                },
                                {
                                    name: 'computeStyles',
                                    options: {
                                        gpuAcceleration: false,
                                    },
                                },
                            ]}
                        >
                            <ClickAwayListener onClickAway={() => setCalendarOpen(false)}>
                                <Box 
                                    sx={{ 
                                        p: 0, 
                                        mb: 1,
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        backgroundColor: 'white',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                        position: 'relative',
                                        '& .react-datepicker': {
                                            fontFamily: 'Gotham',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            width: '290px',
                                            position: 'relative',
                                        },
                                        '& .react-datepicker__header': {
                                            backgroundColor: '#f8f9fa',
                                            borderBottom: '1px solid #e9ecef',
                                            borderRadius: '8px 8px 0 0',
                                            padding: '8px 0',
                                        },
                                        '& .react-datepicker__current-month': {
                                            color: '#3c5471',
                                            fontWeight: 'bold',
                                            fontSize: '16px',
                                            margin: '8px 0',
                                        },
                                        '& .react-datepicker__day-name': {
                                            color: '#6c757d',
                                            fontWeight: '600',
                                            width: '36px',
                                            height: '36px',
                                            lineHeight: '36px',
                                            margin: '2px',
                                            fontSize: '13px',
                                        },
                                        '& .react-datepicker__day': {
                                            color: '#3c5471',
                                            borderRadius: '4px',
                                            margin: '2px',
                                            width: '36px',
                                            height: '36px',
                                            lineHeight: '36px',
                                            fontSize: '14px',
                                            '&:hover': {
                                                backgroundColor: '#e3f2fd',
                                            },
                                            '&.react-datepicker__day--selected': {
                                                backgroundColor: '#2196f3',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#1976d2',
                                                },
                                            },
                                            '&.react-datepicker__day--keyboard-selected': {
                                                backgroundColor: '#2196f3',
                                                color: 'white',
                                            },
                                            '&.react-datepicker__day--disabled': {
                                                color: '#ccc',
                                                cursor: 'not-allowed',
                                            },
                                            '&.react-datepicker__day--outside-month': {
                                                color: '#ccc',
                                            },
                                        },
                                        '& .react-datepicker__navigation': {
                                            top: '8px',
                                            '& .react-datepicker__navigation-icon': {
                                                borderColor: '#3c5471',
                                                '&:hover': {
                                                    borderColor: '#1976d2',
                                                },
                                            },
                                        },
                                        '& .react-datepicker__month-select, & .react-datepicker__year-select': {
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            margin: '0 4px',
                                            fontSize: '14px',
                                            backgroundColor: 'white',
                                        },
                                        '& .react-datepicker__month-container': {
                                            backgroundColor: 'white',
                                        },
                                        '& .react-datepicker__month': {
                                            margin: '8px',
                                        },
                                        '& .react-datepicker__triangle': {
                                            display: 'none',
                                        },
                                    }}
                                >
                                    <ReactDatePicker
                                        inline
                                        selected={form.dueDate ? safeDateParse(form.dueDate) : null}
                                        onChange={handleCalendarChange}
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={100}
                                        minDate={new Date()} // Prevent selecting past dates
                                        dateFormat="MM/dd/yyyy"
                                        showMonthDropdown
                                        dropdownMode="select"
                                        calendarStartDay={1} // Start week on Monday
                                        shouldCloseOnSelect={true}
                                    />
                                </Box>
                            </ClickAwayListener>
                        </Popper>
                    </Grid>

                    <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                                label="Due Time"
                                value={form.dueTime}
                                onChange={(newTime) => {
                                    setForm(prev => ({ ...prev, dueTime: newTime }));
                                    setIsFormDirty(true);
                                }}
                                disabled={isLoading}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            name="priorityId"
                            select
                            label="Priority"
                            fullWidth
                            value={form.priorityId}
                            onChange={(e) => {
                                const value = Number((e.target as HTMLInputElement).value);
                                setForm((prev) => ({ ...prev, priorityId: value }));
                                setIsFormDirty(true);
                            }}
                            disabled={isPrioritiesLoading || isLoading}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } },
                                    disableScrollLock: true,
                                },
                            }}
                        >
                            {isPrioritiesLoading ? (
                                <MenuItem disabled value="">
                                    Loading priorities...
                                </MenuItem>
                            ) : noPriorities ? (
                                <MenuItem disabled value="">
                                    No priorities found
                                </MenuItem>
                            ) : (
                                normalizedPriorities.map((priority: any) => (
                                    <MenuItem value={priority.id} key={priority.id}>
                                        {priority.label ?? priority.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            name="statusId"
                            select
                            label="Status"
                            fullWidth
                            value={form.statusId}
                            onChange={(e) => {
                                const value = Number((e.target as HTMLInputElement).value);
                                setForm((prev) => ({ ...prev, statusId: value }));
                                setIsFormDirty(true);
                            }}
                            disabled={isStatusesLoading || isLoading}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } },
                                    disableScrollLock: true,
                                },
                            }}
                        >
                            {isStatusesLoading ? (
                                <MenuItem disabled value="">
                                    Loading statuses...
                                </MenuItem>
                            ) : noStatuses ? (
                                <MenuItem disabled value="">
                                    No statuses found
                                </MenuItem>
                            ) : (
                                normalizedStatuses.map((status: any) => (
                                    <MenuItem value={status.id} key={status.id}>
                                        {status.label ?? status.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            options={normalizedCategories}
                            getOptionLabel={(option: any) => option?.label || option?.name || ''}
                            isOptionEqualToValue={(option: any, value: any) =>
                                getCategoryIri(option) === getCategoryIri(value)
                            }
                            value={selectedCategory}
                            onChange={(_, value: any) => {
                                setForm((prev) => ({ ...prev, categoryIri: value ? getCategoryIri(value) : '' }));
                                setIsFormDirty(true);
                                setErrors((prev) => ({ ...prev, category: '' }));
                            }}
                            disableClearable
                            openOnFocus
                            selectOnFocus
                            clearOnBlur={false}
                            handleHomeEndKeys
                            disabled={isCategoriesLoading || isLoading}
                            noOptionsText={isCategoriesLoading ? 'Loading categories...' : 'No categories found'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    required
                                    error={!!errors.category}
                                    helperText={
                                        errors.category || (noCategories ? 'No categories found' : '')
                                    }
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            name="noteMessage"
                            label="Notes"
                            multiline
                            rows={3}
                            fullWidth
                            value={form.noteMessage}
                            onChange={handleChange}
                            disabled={isLoading}
                            inputProps={{ maxLength: 500 }}
                            error={!!errors.noteMessage}
                            helperText={errors.noteMessage}
                        />
                    </Grid>
                </Grid>
            </form>

            <Dialog
                open={openBackDialog}
                onClose={() => handleDialogClose(false)}
            >
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    You have unsaved changes. Would you like to save before leaving?
                </DialogContent>
                <DialogActions>
                    <Button style={{
                        backgroundColor: 'rgb(68, 99, 136)',
                        color: 'white',
                        padding: '5px 12px',
                    }} onClick={() => handleDialogClose(false)}>Cancel</Button>
                    <Button onClick={() => handleDialogClose(true)} style={{
                        backgroundColor: 'rgb(68, 99, 136)',
                        color: 'white',
                        padding: '5px 12px',
                    }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default EditToDo;
